import React, { useState, useMemo } from 'react';
import { Pencil, Plus, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  IconButton,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  Paper,
  InputAdornment,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import useAccountingFirms from '../../../hooks/purchaseInvoices/useAccountingFirms.js';
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import AccountantsList from './AccountantsList.js';
import AccountantsManager from './AccountantsManager.js';
import {
  StyledContainer,
  StyledCard,
  StyledTableContainer,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  softColors
} from "../../../styles/purchaseInvoices/pages/commonTableStyles.js";
import ConfirmationDialog from '../../../components/purchaseInvoices/pages/ConfirmationDialog.js';
import { styles } from '../../../styles/purchaseInvoices/pages/accountingFirmsStyles.js';

const AccountingFirms = () => {
  const { isDesktop } = useResponsive();
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    firms,
    isLoading,
    isLoadingEmail,
    error,
    newFirm,
    setNewFirm,
    editingFirm,
    setEditingFirm,
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    handleAdd,
    handleUpdate,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    isDeleteDialogOpen,
    firmToDelete,
    isDeleting,
    handleEdit,
    addAccountantToNewFirm,
    removeAccountantFromNewFirm,
    updateAccountantInNewFirm,
    addAccountantToEditFirm,
    removeAccountantFromEditFirm,
    updateAccountantInEditFirm,
    updateFirmMutation
  } = useAccountingFirms();

  const handleChangePrimaryInView = async (firm, newPrimaryAccountant) => {
    const updatedAccountants = firm.accountants.map(acc => ({
      ...acc,
      isPrimary: (acc._id && acc._id === newPrimaryAccountant._id) ||
        (!acc._id && acc.accountant === newPrimaryAccountant.accountant)
    }));

    const updatedFirm = {
      ...firm,
      accountants: updatedAccountants,
      _silentUpdate: true
    };

    updateFirmMutation.mutate(updatedFirm);
  };

  const columns = [
    {
      accessorKey: 'companyName',
      header: 'שם החברה',
    },
    {
      accessorKey: 'companyId',
      header: 'ח.פ',
      cell: ({ value }) => value || '-'
    },
    {
      accessorKey: 'companyNumber',
      header: 'מספר חברה',
      cell: ({ value }) => value || '-'
    },
    {
      accessorKey: 'vatFileNumber',
      header: 'מספר תיק במע"מ',
      cell: ({ value }) => value || '-'
    },
    {
      accessorKey: 'accountants',
      header: 'מנהלות חשבונות',
      cell: ({ value, row }) => (
        <AccountantsList
          accountants={value}
          editable={true}
          onChangePrimary={(newPrimary) => handleChangePrimaryInView(row, newPrimary)}
        />
      )
    },
    {
      accessorKey: 'accountants',
      header: 'התראות מייל',
      cell: ({ value }) => {
        const primaryAccountant = value?.find(acc => acc.isPrimary) || value?.[0];
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {primaryAccountant?.receiveEmailNotifications ? (
              <Typography color="success.main" fontSize="18px">✓</Typography>
            ) : (
              <Typography color="error.main" fontSize="18px">✗</Typography>
            )}
          </Box>
        );
      }
    },
    {
      id: 'actions',
      header: 'פעולות',
      cell: ({ row }) => (
        <Box display="flex" justifyContent="center" gap={1}>
          <IconButton
            aria-label="edit"
            onClick={() => handleEdit(row)}
            size="small"
            sx={{ color: softColors.primary }}
          >
            <Pencil size={18} color="#1976d2" />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => handleDeleteClick(row._id, row.companyName)}
            size="small"
            sx={styles.deleteButtonIcon}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  const renderMobileCard = (firm) => {
    const isExpanded = expandedRow === firm._id;
    const primaryAccountant = firm.accountants?.find(acc => acc.isPrimary) || firm.accountants?.[0];

    return (
      <Paper key={firm._id} elevation={1} sx={styles.mobileCard}>
        <Box sx={styles.mobileCardHeader}>
          <Typography variant="subtitle1" fontWeight="bold">
            {firm.companyName}
          </Typography>

          <Box sx={styles.mobileCardButtonsContainer}>
            <IconButton
              aria-label="edit"
              onClick={() => handleEdit(firm)}
              size="small"
              sx={{ color: softColors.primary }}
            >
              <Pencil size={18} color="#1976d2" />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => handleDeleteClick(firm._id, firm.companyName)}
              size="small"
              sx={styles.deleteButtonIcon}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {firm.companyId && (
          <Box>
            <Typography variant="body2" color="text.secondary">ח.פ:</Typography>
            <Typography variant="body1">{firm.companyId}</Typography>
          </Box>
        )}

        {firm.companyNumber && (
          <Box>
            <Typography variant="body2" color="text.secondary">מספר חברה:</Typography>
            <Typography variant="body1">{firm.companyNumber}</Typography>
          </Box>
        )}

        {firm.vatFileNumber && (
          <Box>
            <Typography variant="body2" color="text.secondary">מספר תיק במע"מ:</Typography>
            <Typography variant="body1">{firm.vatFileNumber}</Typography>
          </Box>
        )}

        {primaryAccountant && (
          <Box>
            <Typography variant="body2" color="text.secondary">מנהלת חשבונות ראשית:</Typography>
            <Typography variant="body1">{primaryAccountant.name || primaryAccountant.accountant}</Typography>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <IconButton
            size="small"
            onClick={() => setExpandedRow(isExpanded ? null : firm._id)}
            sx={styles.expandButton}
          >
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </IconButton>
          <Collapse in={isExpanded} timeout="auto">
            <Box sx={styles.expandedContent}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                כל מנהלות החשבונות:
              </Typography>
              <AccountantsList accountants={firm.accountants} />
            </Box>
          </Collapse>
        </Box>
      </Paper>
    );
  };

  const filteredFirms = useMemo(() => {
    if (!searchTerm.trim() || !firms?.accountingFirms?.length) {
      return firms?.accountingFirms || [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return firms.accountingFirms.filter(firm => {
      const companyMatch =
        firm.companyName.toLowerCase().includes(lowerSearchTerm) ||
        (firm.companyId && firm.companyId.toLowerCase().includes(lowerSearchTerm)) ||
        (firm.companyNumber && firm.companyNumber.toLowerCase().includes(lowerSearchTerm)) ||
        (firm.vatFileNumber && firm.vatFileNumber.toLowerCase().includes(lowerSearchTerm));

      const accountantsMatch = firm.accountants?.some(acc =>
        acc.name?.toLowerCase().includes(lowerSearchTerm) ||
        acc.accountant?.toLowerCase().includes(lowerSearchTerm) ||
        acc.email?.toLowerCase().includes(lowerSearchTerm)
      );

      return companyMatch || accountantsMatch;
    });
  }, [firms?.accountingFirms, searchTerm]);

  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="h6" sx={styles.loadingText}>
          טוען נתונים...
        </Typography>
      </Box>
    );
  }

  return (
    <StyledContainer>
      <StyledCard>
        <Box sx={styles.headerBox}>
          <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
            <TextField
              placeholder="חיפוש חברה"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ dir: "rtl" }}
              sx={styles.searchInput}
            />
          </Box>

          <Button
            onClick={() => {
              setNewFirm({
                companyName: "",
                companyId: "",
                companyNumber: "",
                vatFileNumber: "",
                accountants: [
                  {
                    name: "",
                    accountant: "",
                    email: "",
                    receiveEmailNotifications: true,
                    isPrimary: true
                  }
                ]
              });
              setIsAddModalOpen(true);
            }}
            color="primary"
            size="medium"
            startIcon={<Plus size={20} strokeWidth={3} />}
            sx={styles.addButtonResponsive}
          >
            הוספת חברה
          </Button>
        </Box>

        {error ? (
          <Box sx={styles.errorBox}>
            <Typography variant="h6" color="error" sx={styles.errorText}>
              שגיאה בטעינת הנתונים
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              נסה שנית
            </Button>
          </Box>
        ) : isDesktop ? (
          <StyledTableContainer sx={styles.tableMinHeight}>
            <Table dir="rtl">
              <TableHead>
                <TableRow>
                  {columns.map(column => (
                    <StyledTableHeadCell
                      key={column.id || column.accessorKey}
                      align="center"
                    >
                      {column.header}
                    </StyledTableHeadCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFirms.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={columns.length} align="center">
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין חברות להצגה"}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredFirms.map((row) => (
                    <StyledTableRow key={row._id}>
                      {columns.map((column, columnIndex) => (
                        <StyledTableCell
                          key={columnIndex}
                          align="center"
                          sx={{
                            verticalAlign: column.accessorKey === 'accountants' ? 'top' : 'middle',
                            py: column.accessorKey === 'accountants' ? 2 : 1.5
                          }}
                        >
                          {column.cell && typeof column.cell === 'function' ?
                            column.cell({ row, value: row[column.accessorKey] }) :
                            row[column.accessorKey] || '-'
                          }
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        ) : (
          <Box sx={styles.mobileCardContainer}>
            {filteredFirms.length === 0 ? (
              <Box sx={styles.emptyBox}>
                <Typography variant="body1" color="text.secondary">
                  {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין חברות להצגה"}
                </Typography>
              </Box>
            ) : (
              filteredFirms.map(firm => renderMobileCard(firm))
            )}
          </Box>
        )}

        <Box sx={styles.footerBox}>
          <Typography variant="body2" color="text.secondary" align="center">
            {filteredFirms.length > 0 ? `סה"כ ${filteredFirms.length} חברות` : ''}
          </Typography>
        </Box>
      </StyledCard>

      <Dialog
        open={isAddModalOpen}
        onClose={() => {
          setNewFirm({
            companyName: "",
            companyId: "",
            companyNumber: "",
            vatFileNumber: "",
            accountants: [
              {
                name: "",
                accountant: "",
                email: "",
                receiveEmailNotifications: true,
                isPrimary: true
              }
            ]
          });
          setIsAddModalOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: styles.dialog['& .MuiDialog-paper'] }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <Typography sx={styles.dialogTitleText}>
            הוספת חברה חדשה
          </Typography>

          <IconButton
            onClick={() => {
              setNewFirm({
                companyName: "",
                companyId: "",
                companyNumber: "",
                vatFileNumber: "",
                accountants: [
                  {
                    name: "",
                    accountant: "",
                    email: "",
                    receiveEmailNotifications: true,
                    isPrimary: true
                  }
                ]
              });
              setIsAddModalOpen(false);
            }}
            sx={styles.dialogCloseButton}
            size="small"
          >
            <X size={20} strokeWidth={2.5} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          <div style={styles.formContainer}>
            <div>
              <label style={styles.formLabel}>
                שם החברה: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="שם החברה"
                style={styles.formInput}
                value={newFirm.companyName}
                onChange={(e) => setNewFirm(prev => ({ ...prev, companyName: e.target.value }))}
              />
            </div>
            <div>
              <label style={styles.formLabel}>ח.פ:</label>
              <input
                type="text"
                placeholder="ח.פ"
                style={styles.formInput}
                value={newFirm.companyId || ""}
                onChange={(e) => setNewFirm(prev => ({ ...prev, companyId: e.target.value }))}
              />
            </div>
            <div>
              <label style={styles.formLabel}>מספר חברה:</label>
              <input
                type="text"
                placeholder="מספר חברה"
                style={styles.formInput}
                value={newFirm.companyNumber || ""}
                onChange={(e) => setNewFirm(prev => ({ ...prev, companyNumber: e.target.value }))}
              />
            </div>
            <div>
              <label style={styles.formLabel}>מספר תיק במע"מ:</label>
              <input
                type="text"
                placeholder="מספר תיק במע״מ"
                style={styles.formInput}
                value={newFirm.vatFileNumber || ""}
                onChange={(e) => setNewFirm(prev => ({ ...prev, vatFileNumber: e.target.value }))}
              />
            </div>

            <Box sx={{ mt: 3, mb: 2 }}>
              <AccountantsManager
                accountants={newFirm.accountants}
                onAdd={addAccountantToNewFirm}
                onRemove={removeAccountantFromNewFirm}
                onUpdate={updateAccountantInNewFirm}
                isLoadingEmail={isLoadingEmail}
              />
            </Box>

            <Box sx={styles.dialogActions}>
              <Button
                onClick={() => {
                  setNewFirm({
                    companyName: "",
                    companyId: "",
                    companyNumber: "",
                    vatFileNumber: "",
                    accountants: [
                      {
                        name: "",
                        accountant: "",
                        email: "",
                        receiveEmailNotifications: true,
                        isPrimary: true
                      }
                    ]
                  });
                  setIsAddModalOpen(false);
                }}
                sx={styles.dialogCancelButton}
              >
                ביטול
              </Button>
              <Button
                onClick={handleAdd}
                variant="contained"
                sx={styles.dialogSubmitButton}
              >
                הוסף
              </Button>
            </Box>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: styles.dialog['& .MuiDialog-paper'] }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <Typography sx={styles.dialogTitleText}>
            עריכת חברה
          </Typography>

          <IconButton
            onClick={() => setIsEditModalOpen(false)}
            sx={styles.dialogCloseButton}
            size="small"
          >
            <X size={20} strokeWidth={2.5} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          {editingFirm && (
            <div style={styles.formContainer}>
              <div>
                <label style={styles.formLabel}>
                  שם החברה: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="שם החברה"
                  style={styles.formInput}
                  value={editingFirm.companyName}
                  onChange={(e) => setEditingFirm(prev => ({ ...prev, companyName: e.target.value }))}
                />
              </div>
              <div>
                <label style={styles.formLabel}>ח.פ:</label>
                <input
                  type="text"
                  placeholder="ח.פ (לא חובה)"
                  style={styles.formInput}
                  value={editingFirm.companyId || ""}
                  onChange={(e) => setEditingFirm(prev => ({ ...prev, companyId: e.target.value }))}
                />
              </div>
              <div>
                <label style={styles.formLabel}>מספר חברה:</label>
                <input
                  type="text"
                  placeholder="מספר חברה (לא חובה)"
                  style={styles.formInput}
                  value={editingFirm.companyNumber || ""}
                  onChange={(e) => setEditingFirm(prev => ({ ...prev, companyNumber: e.target.value }))}
                />
              </div>
              <div>
                <label style={styles.formLabel}>מספר תיק במע"מ:</label>
                <input
                  type="text"
                  placeholder="מספר תיק במע״מ (לא חובה)"
                  style={styles.formInput}
                  value={editingFirm.vatFileNumber || ""}
                  onChange={(e) => setEditingFirm(prev => ({ ...prev, vatFileNumber: e.target.value }))}
                />
              </div>

              <Box sx={{ mt: 3, mb: 2 }}>
                <AccountantsManager
                  accountants={editingFirm.accountants}
                  onAdd={addAccountantToEditFirm}
                  onRemove={removeAccountantFromEditFirm}
                  onUpdate={updateAccountantInEditFirm}
                  isLoadingEmail={isLoadingEmail}
                />
              </Box>

              <Box sx={styles.dialogActions}>
                <Button
                  onClick={() => setIsEditModalOpen(false)}
                  sx={styles.dialogCancelButton}
                >
                  ביטול
                </Button>
                <Button
                  onClick={handleUpdate}
                  variant="contained"
                  sx={styles.dialogSubmitButton}
                >
                  עדכון
                </Button>
              </Box>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        entityName={firmToDelete?.companyName || ""}
        entityType="חברה"
        open={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isPending={isDeleting}
      />
    </StyledContainer>
  );
};

export default AccountingFirms;